import { unreadable } from './shared.ts';

/**
 * §8's Search page.
 *
 * The **suppression** sentences are the ones worth reading carefully, because they are the visible half
 * of a measurement *and* the reason a row of controls is greyed out in front of the reader: `channel:`,
 * `user:` and `subscriptions:` are answered as special cases rather than as searches, and combining one
 * with a filter does not ignore the filter — it *corrupts* the answer. Measured: `channel:UC… black
 * hole` returns 28 rows all from that channel; adding `duration=long` returns 19 rows from 10 channels,
 * 1 of them the one asked for.
 *
 * The **duration** details are measured too, rather than taken from YouTube's help pages: `short` is
 * everything under 240 s, `medium` 240–507 s, `long` 2 183 s and up. So the menu says 4 and 20 minutes
 * instead of leaving a reader to guess whether "medium" is five minutes or fifty.
 */
export const search = {
  type: {
    all: 'Everything',
    video: 'Videos',
    channel: 'Channels',
    playlist: 'Playlists',
    /** Not "Films": the filter is YouTube's `movie`, and it returns ordinary `type:"video"` records. */
    movie: 'Movies',
  },

  sort: { relevance: 'Relevance', views: 'View count' },

  date: {
    any: 'Any time',
    hour: 'Last hour',
    today: 'Today',
    week: 'This week',
    month: 'This month',
    year: 'This year',
  },

  duration: {
    any: 'Any length',
    short: 'Short',
    medium: 'Medium',
    long: 'Long',
    shortDetail: 'under 4 minutes',
    mediumDetail: '4–20 minutes',
    longDetail: 'over 20 minutes',
  },

  feature: {
    hd: 'HD',
    subtitles: 'Subtitles / CC',
    creativeCommons: 'Creative Commons',
    threeD: '3D',
    live: 'Live',
    purchased: 'Purchased',
    fourK: '4K',
    threeSixty: '360°',
    location: 'Location',
    hdr: 'HDR',
  },

  /** What an unset filter shows on its trigger — the menu's name is already beside it. */
  anyValue: 'Any',

  menu: {
    type: 'Type',
    sort: 'Sort',
    date: 'Upload date',
    duration: 'Length',
    features: 'Features',
  },

  suppression: {
    scoping: (names: string): string =>
      `Your query uses ${names}, which YouTube answers as a different kind of search. Adding a filter to it does not narrow the results. It mixes in other channels' videos while the page still looks like a channel search, so the filters below are switched off for this query.`,
    special: (names: string): string =>
      `Your query uses ${names}, which is not answered as a search at all, so the filters below do not apply and are switched off.`,
    scopingShort: (names: string): string =>
      `Unavailable while the query uses ${names}: combining them returns other channels' results.`,
    specialShort: (names: string): string =>
      `Unavailable while the query uses ${names}, which is not answered as a search.`,
    subscriptions:
      '“subscriptions:” cannot be searched here. A search never knows who you are, so it comes back empty instead of failing. Your Subscriptions page is the place to look.',
    subscriptionsAction: 'Go to Subscriptions',
    /** Only what the channel page actually has: there is no search box *inside* a channel yet. */
    channel:
      'Browsing the channel itself does the same job without the operator: its Videos, Shorts, Live and Playlists tabs page through everything the channel has published.',
    channelAction: 'Open that channel',
  },

  allDropped: {
    title: 'Could not read these results',
    detail: 'The instance answered, and nothing in it could be shown.',
  },

  empty: {
    title: (query: string): string => `No results for “${query}”`,
    /** A dead-end operator gets neither piece of advice — the search never ran. */
    deadEnd: 'That operator always comes back empty here, so this says nothing about what exists.',
    filtered: 'Nothing matched with these filters. Clearing them searches everything.',
    plain: 'Nothing matched. Check the spelling, or try fewer words. There is no fuzzy matching here.',
  },

  problem: {
    notFound: {
      title: 'This instance has no search',
      detail: 'It is not served here. Some instances leave parts of the app out.',
      retryable: false,
    },
    unreadable: unreadable('Could not read the results'),
    disabled: {
      title: 'Search is switched off on this instance',
      detail: 'Its administrator turned it off. Every other page works normally.',
      retryable: false,
    },
  },

  page: {
    resultsFor: 'Results for',
    /** The grid's accessible name, which was built in the JSX and so said "Results for" in English. */
    gridLabel: (query: string): string => `Results for “${query}”`,
    filtersLabel: 'Filter results',
    searching: (query: string): string => `Searching for ${query}…`,
    exhausted: 'That was the last page',
    /** Why a page number past the end is empty rather than broken. Also an inline literal until now. */
    exhaustedDetail: (page: number): string =>
      `The instance has no page ${page} for this search. There is no result total in this API, so the only way to find the end is to reach it.`,
    /**
     * The reset beside the filter cluster, in its two shapes. The count-bearing one was
     * `filter{n === 1 ? '' : 's'}` in the JSX — the exact construction this catalogue's own header calls
     * untranslatable, since a language whose plural is not a suffix has nowhere to put it.
     */
    clearFilters: 'Clear the filters',
    clearFilterCount: (count: number): string => `Clear ${count} filter${count === 1 ? '' : 's'}`,
    backToPage: (page: number): string => `Back to page ${page}`,
    /** No total: "so far" is the strongest true claim, and the end line states only what was proved. */
    loadMore: (soFar: number): string => `Load more (${soFar} so far)`,
    loadingMore: 'Loading…',
    allLoaded: (count: number): string => `That is all ${count} results this search returned.`,
  },
} as const;
